import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from '~/composables/store'
import { useQuery, useMutation } from '@urql/vue'
import { gql, CombinedError } from '@urql/core'

export interface KnowledgeBaseEntry {
  id: string
  title: string
  content: string
  pathPattern: string
  createdAt: string
  updatedAt: string
  createdBy?: string // Make optional since it's not in the schema
  status?: string
}

// Add this interface near the top with other interfaces
interface KnowledgeBaseEntriesResponse {
  knowledgeBaseEntriesByPath: Array<{
    id: string
    title: string
    content: string
    pathPattern: string
    createdAt: string
    updatedAt: string
  }>
}

// Create shared state outside the function
const isModalOpen = ref(false)
const isEditing = ref(false)
const currentEntry = ref<KnowledgeBaseEntry | null>(null)
const selectedEntryId = ref<string | null>(null)
const entries = ref<KnowledgeBaseEntry[]>([])
const isLoading = ref(false)
const error = ref<Error | null>(null)

// GraphQL Documents using gql tag
const KnowledgeBaseEntriesByPathDocument = gql`
  query getKnowledgeBaseEntriesByPath($path: String!) {
    knowledgeBaseEntriesByPath(path: $path) {
      id
      title
      content
      pathPattern
      createdAt
      updatedAt
    }
  }
`

// New query for all knowledge base entries
const AllKnowledgeBaseEntriesDocument = gql`
  query getAllKnowledgeBaseEntries {
    knowledgeBaseEntries {
      data {
        id
        title
        content
        pathPattern
        createdAt
        updatedAt
      }
      meta {
        total
        currentPage
        lastPage
        perPage
        prev
        next
      }
    }
  }
`

const CreateKnowledgeBaseEntryDocument = gql`
  mutation createKnowledgeBaseEntry($data: CreateKnowledgeBaseEntryInput!) {
    createKnowledgeBaseEntry(data: $data) {
      id
      title
      content
      pathPattern
      createdAt
      updatedAt
    }
  }
`

const UpdateKnowledgeBaseEntryDocument = gql`
  mutation updateKnowledgeBaseEntry($data: UpdateKnowledgeBaseEntryInput!) {
    updateKnowledgeBaseEntry(data: $data) {
      id
      title
      content
      pathPattern
      createdAt
      updatedAt
    }
  }
`

const RemoveKnowledgeBaseEntryDocument = gql`
  mutation removeKnowledgeBaseEntry($id: Int!) {
    removeKnowledgeBaseEntry(id: $id) {
      id
      title
    }
  }
`

const RequestKnowledgeBaseEntryDocument = gql`
  mutation requestKnowledgeBaseEntry($data: RequestKnowledgeBaseEntryInput!) {
    requestKnowledgeBaseEntry(data: $data)
  }
`

// This is now connected to the API
export function useKnowledgeBase() {
  const store = useStore()
  const route = useRoute()

  // Create mutation clients
  const createMutation = useMutation(CreateKnowledgeBaseEntryDocument)
  const updateMutation = useMutation(UpdateKnowledgeBaseEntryDocument)
  const removeMutation = useMutation(RemoveKnowledgeBaseEntryDocument)
  const requestMutation = useMutation(RequestKnowledgeBaseEntryDocument)

  // Setup the query client once (not recreated on each fetch)
  const queryVariables = ref({ path: '' })
  const entriesQuery = useQuery({
    query: KnowledgeBaseEntriesByPathDocument,
    variables: queryVariables,
    pause: true,
    requestPolicy: 'network-only',
  })

  // Fetch entries by path
  const fetchEntriesByPath = async (path: string) => {
    isLoading.value = true
    error.value = null

    try {
      if (!store.auth?.authToken) {
        console.warn('No auth token available for knowledge base query')
      }

      queryVariables.value.path = path

      // First resume the query to ensure it's not paused
      entriesQuery.resume()

      // Execute the query and get the result
      const result = await entriesQuery.executeQuery()

      // Check for errors
      if (result.error.value) {
        console.error('GraphQL Error:', result.error.value)
        error.value = new Error('Failed to fetch knowledge base entries')
        return
      }

      // Access the data from the result
      const queryData = result.data.value

      // Process the data if it exists
      if (queryData?.knowledgeBaseEntriesByPath) {
        entries.value = queryData.knowledgeBaseEntriesByPath.map((entry) => ({
          ...entry,
          id: entry.id.toString(),
          createdBy: store.auth?.user?.name || 'System',
        }))
      } else {
        entries.value = []
      }
    } catch (err) {
      console.error('Exception fetching knowledge base entries:', err)
      error.value = err instanceof Error ? err : new Error(String(err))
    } finally {
      isLoading.value = false
      // Pause the query again after we're done
      entriesQuery.pause()
    }
  }

  // Setup the query client for all entries
  const allEntriesQuery = useQuery({
    query: AllKnowledgeBaseEntriesDocument,
    pause: true, // Start paused, we'll execute manually
    requestPolicy: 'network-only', // Force network request to avoid caching issues
  })

  // Fetch all knowledge base entries (not filtered by path)
  const fetchAllEntries = async () => {
    isLoading.value = true
    error.value = null

    try {
      // Check if auth token exists
      if (!store.auth?.authToken) {
        console.warn('No auth token available for knowledge base query')
      }

      // Execute the query for all entries
      const result = await allEntriesQuery.executeQuery()

      // Check for errors
      if (result.error.value) {
        console.error('GraphQL Error:', result.error.value)
        error.value = new Error('Failed to fetch knowledge base entries')
        return
      }

      const data = result.data.value

      // Process the results
      if (data?.knowledgeBaseEntries?.data) {
        entries.value = data.knowledgeBaseEntries.data.map((entry) => ({
          ...entry,
          id: entry.id.toString(), // Convert numeric ID to string for compatibility
          createdBy: store.auth?.user?.name || 'System', // Add createdBy since it's not in schema
        }))
      } else {
        console.warn('No entries found in the response:', data)
        entries.value = []
      }
    } catch (err) {
      console.error('Exception fetching all knowledge base entries:', err)
      error.value = err instanceof Error ? err : new Error(String(err))
    } finally {
      isLoading.value = false
    }
  }

  // Find entries that match the current route path
  const matchingEntries = computed(() => entries.value)

  // We no longer automatically fetch on route changes
  // Entries will be fetched when the knowledge base button is clicked instead

  const selectedEntry = computed(() => {
    if (selectedEntryId.value) {
      return (
        matchingEntries.value.find(
          (entry) => entry.id === selectedEntryId.value,
        ) || null
      )
    }
    return null
  })

  const currentEntryForEditing = computed({
    get: () => currentEntry.value,
    set: (value) => {
      currentEntry.value = value
    },
  })

  // Check if the user has edit permissions
  const hasEditPermissions = computed(() => {
    // For demonstration purposes, we'll check if the user has a specific permission
    // In a real implementation, this would check against a dedicated permission
    const userPermissions = store.auth?.user?.permissions || []

    // For debugging, also check if permissions exists at all
    if (!store.auth?.user?.permissions) {
      console.warn('No permissions array found in user object')
    }

    return (
      userPermissions.includes('knowledgeBaseEntry') ||
      userPermissions.includes('knowledgeBaseEntry:w') ||
      userPermissions.includes('**:w')
    )
  })

  function openModal() {
    isModalOpen.value = true
    // Clear selected entry when opening
    selectedEntryId.value = null
  }

  function closeModal() {
    isModalOpen.value = false
    isEditing.value = false
    currentEntry.value = null
    selectedEntryId.value = null
  }

  function selectEntry(id: string) {
    selectedEntryId.value = id
  }

  function startEditing(entry: KnowledgeBaseEntry | null = null) {
    if (!hasEditPermissions.value) return

    currentEntry.value = entry
    isEditing.value = true
  }

  function createNewEntry() {
    if (!hasEditPermissions.value) return

    currentEntry.value = {
      id: 'new',
      title: '',
      content: '',
      pathPattern: route.path,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: store.auth?.user?.name || 'Unknown User',
      status: 'draft',
    }

    isEditing.value = true
  }

  async function saveEntry(entry: KnowledgeBaseEntry) {
    isLoading.value = true
    error.value = null

    try {
      if (entry.id === 'new') {
        // Create new entry
        const createInput = {
          title: entry.title,
          content: entry.content,
          pathPattern: entry.pathPattern,
          status: entry.status || 'draft',
        }

        // Execute the mutation
        const result = await createMutation.executeMutation({
          data: createInput,
        })

        if (result.data?.createKnowledgeBaseEntry) {
          // Add the new entry to our local array
          const newEntry = {
            ...result.data.createKnowledgeBaseEntry,
            id: result.data.createKnowledgeBaseEntry.id.toString(),
            createdBy: store.auth?.user?.name || 'System', // Add createdBy since it's not in schema
          }
          entries.value.push(newEntry)
        } else {
          console.error(
            'No data returned from create mutation, but no error thrown',
          )
        }

        if (result.error) {
          console.error('Error creating entry:', result.error)
          throw new Error(result.error.message)
        }
      } else {
        // Update existing entry
        const updateInput = {
          id: parseInt(entry.id),
          title: entry.title,
          content: entry.content,
          pathPattern: entry.pathPattern,
          status: entry.status,
        }

        // Execute the mutation
        const result = await updateMutation.executeMutation({
          data: updateInput,
        })

        if (result.data?.updateKnowledgeBaseEntry) {
          // Update the entry in our local array
          const index = entries.value.findIndex((e) => e.id === entry.id)
          if (index >= 0) {
            entries.value[index] = {
              ...result.data.updateKnowledgeBaseEntry,
              id: result.data.updateKnowledgeBaseEntry.id.toString(),
              createdBy:
                entries.value[index].createdBy ||
                store.auth?.user?.name ||
                'System',
            }
          }
        } else {
          console.error(
            'No data returned from update mutation, but no error thrown',
          )
        }

        if (result.error) {
          console.error('Error updating entry:', result.error)
          throw new Error(result.error.message)
        }
      }

      isEditing.value = false
      currentEntry.value = null
      isLoading.value = false
    } catch (err) {
      console.error('Error saving knowledge base entry:', err)
      error.value = err instanceof Error ? err : new Error(String(err))
      isLoading.value = false
    }
  }

  async function deleteEntry(id: string) {
    if (!hasEditPermissions.value) return

    isLoading.value = true
    error.value = null

    try {
      const result = await removeMutation.executeMutation({
        id: parseInt(id),
      })

      if (result.error) {
        console.error('Error deleting entry:', result.error)
        throw new Error(result.error.message)
      }

      if (result.data?.removeKnowledgeBaseEntry) {
        // Remove from our local array
        const index = entries.value.findIndex((e) => e.id === id)
        if (index >= 0) {
          entries.value.splice(index, 1)
        }

        if (currentEntry.value?.id === id) {
          currentEntry.value = null
          isEditing.value = false
        }

        if (selectedEntryId.value === id) {
          selectedEntryId.value = null
        }
      } else {
        console.error(
          'No data returned from delete mutation, but no error thrown',
        )
      }

      isLoading.value = false
    } catch (err) {
      console.error('Error deleting knowledge base entry:', err)
      error.value = err instanceof Error ? err : new Error(String(err))
      isLoading.value = false
    }
  }

  async function requestEntry(
    content: string,
    pagePath: string,
  ): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const result = await requestMutation.executeMutation({
        data: {
          content,
          pagePath,
        },
      })

      if (result.error) {
        console.error('Error requesting knowledge base entry:', result.error)
        throw new Error(result.error.message)
      }

      isLoading.value = false
      return result.data?.requestKnowledgeBaseEntry || false
    } catch (err) {
      console.error('Error requesting knowledge base entry:', err)
      error.value = err instanceof Error ? err : new Error(String(err))
      isLoading.value = false
      return false
    }
  }

  return {
    isModalOpen,
    isEditing,
    entries,
    matchingEntries,
    selectedEntryId,
    selectedEntry,
    currentEntryForEditing,
    hasEditPermissions,
    isLoading,
    error,
    openModal,
    closeModal,
    selectEntry,
    startEditing,
    createNewEntry,
    saveEntry,
    deleteEntry,
    requestEntry,
    fetchEntriesByPath,
    fetchAllEntries,
  }
}
